// Instrument output — the browser's half of the boat's "which signal goes out
// as which PGN" table (ADR 0194, core ADR-153).
//
// EVERY CHOICE THIS EDITOR OFFERS COMES FROM THE BOAT. The PGN targets ship
// with the core release, the input paths are whatever is live on the bus right
// now, and which gateways can transmit is a property of the hardware. So the
// hook below holds no catalog of its own — it renders the boat's last report,
// and when that report is missing it says so rather than showing an empty form
// whose every choice the boat would refuse.
//
// SAVING IS A CLAIM ABOUT A REQUEST, NOT ABOUT A RESULT. The PUT returns 202
// meaning "the boat has been told". Whether the boat ACCEPTED is answered by
// the mirror's `configDigest` catching up to the digest the save returned —
// which is what `useN2kOutputSave` watches. The boat can refuse (a row its core
// build does not ship, a pin that cannot transmit, another tab that saved
// first), and pretending otherwise would show a confident configuration over
// displays still showing the old one.

import { useCallback, useEffect, useState } from 'react';

import {
  n2kOutputConfigDigest,
  type N2kOutputConfig,
  type N2kOutputStatePayload,
} from '@neomaris/protocol';

import { api } from '../api';

import { apiErrorCode, apiErrorMessage } from './marineToolkit';

export interface N2kOutputResponse {
  generatedAt: string;
  receivedAt: string;
  configDigest: string;
  authority: 'cloud' | 'local';
  state: N2kOutputStatePayload;
}

type OutputState =
  | { kind: 'loading' }
  /** The boat has never reported — we do not know its catalog. */
  | { kind: 'unavailable' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: N2kOutputResponse };

/**
 * Poll interval.
 *
 * The boat re-enqueues its mirror frame roughly every thirty seconds, so
 * reading faster than that buys nothing but load — this is not a live feed and
 * pretending it is would be its own dishonesty. Ten seconds is the compromise:
 * fast enough that a save's confirmation feels prompt, slow enough that it is
 * not three reads per boat frame.
 */
const POLL_MS = 10_000;

export function useN2kOutput(boatId: string) {
  const [state, setState] = useState<OutputState>({ kind: 'loading' });

  const refresh = useCallback(async () => {
    if (!boatId) return;
    try {
      const data = await api<N2kOutputResponse>(`/v1/boats/${boatId}/n2k-output`);
      setState({ kind: 'ready', data });
    } catch (error) {
      if (apiErrorCode(error) === 'n2k_output_unavailable') {
        setState({ kind: 'unavailable' });
      } else {
        setState({ kind: 'error', message: apiErrorMessage(error) });
      }
    }
  }, [boatId]);

  useEffect(() => {
    let active = true;
    const run = () => {
      if (active) void refresh();
    };
    run();
    const timer = window.setInterval(() => {
      if (!document.hidden) run();
    }, POLL_MS);
    const onVisibility = () => {
      if (!document.hidden) run();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      active = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  return { state, refresh };
}

export type SaveOutcome =
  /** Relayed. The boat has been told; the mirror will confirm or refuse. */
  | { kind: 'sent'; configDigest: string }
  /** The boat is unreachable, or it authors its own configuration. */
  | { kind: 'refused'; code: string; message: string }
  | { kind: 'error'; message: string };

/**
 * One save INTENT: a specific document, saved against a specific base, under
 * one idempotency key that survives retries.
 *
 * THE KEY'S LIFETIME IS THE INTENT, NOT THE REQUEST. If it were minted per
 * call, this would happen: the server accepts the PUT and relays it, the boat
 * applies it, and then the response is lost — a dropped connection, a tunnel
 * closing, a phone changing network at anchor. The user presses Save again, a
 * NEW key is minted, the cloud relays the same document a second time, and the
 * boat — whose configuration has already moved on — answers `stale_base`. The
 * page would then tell the owner their change failed while their displays were
 * already showing it.
 *
 * Reusing the key makes the server return its cached 202 instead, so a retry
 * after a lost response is indistinguishable from the response arriving.
 *
 * Reuse is safe for every other outcome too, because the server caches ONLY
 * 2xx (`core/idempotency.ts`): a retry after `boat_offline` re-relays properly
 * once the boat is back, rather than being answered from a stale 409. And
 * because the key is derived from the document, an edit mints a new one — so
 * the plugin's same-key-different-body conflict is unreachable from here.
 */
export interface SaveIntent {
  readonly key: string;
  /** The `(baseDigest, configDigest)` pair this key belongs to. */
  readonly signature: string;
}

/**
 * Mint or reuse the key for saving `config` against `baseDigest`.
 *
 * Pass the previous intent back in; it is reused when the save being retried is
 * the SAME one, and replaced when the user has edited since. Keying on content
 * rather than on a click means a genuine second save of a genuinely different
 * document never collapses onto the first one's cached answer.
 */
export function saveIntentFor(
  config: N2kOutputConfig,
  baseDigest: string | null,
  previous: SaveIntent | null
): SaveIntent {
  const signature = `${baseDigest ?? 'force'}→${n2kOutputConfigDigest(config)}`;
  if (previous && previous.signature === signature) return previous;
  const suffix = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return { key: `n2k-output-${suffix}`, signature };
}

/**
 * Send a whole configuration document.
 *
 * `baseDigest` is the digest the editor loaded — the optimistic-concurrency
 * token. Two tabs (or two owners) editing one boat is not exotic, and a
 * document replace has no merge to save it: without this, the second save
 * silently reverts the first.
 */
export async function saveN2kOutput(
  boatId: string,
  config: N2kOutputConfig,
  baseDigest: string | null,
  intent: SaveIntent
): Promise<SaveOutcome> {
  try {
    const res = await api<{ configDigest: string }>(`/v1/boats/${boatId}/n2k-output`, {
      method: 'PUT',
      body: { config, baseDigest },
      idempotencyKey: intent.key,
    });
    return { kind: 'sent', configDigest: res.configDigest };
  } catch (error) {
    const code = apiErrorCode(error);
    if (code === 'boat_offline' || code === 'boat_authored' || code === 'stale_base') {
      return { kind: 'refused', code, message: apiErrorMessage(error) };
    }
    return { kind: 'error', message: apiErrorMessage(error) };
  }
}

/** Human age, for a mirror that is by nature a little behind. */
export function agoLabel(iso: string, now: number): string {
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return 'unknown';
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 45) return 'just now';
  if (seconds < 90) return 'a minute ago';
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 7200) return 'an hour ago';
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} hours ago`;
  return `${Math.round(seconds / 86_400)} days ago`;
}

/**
 * What the boat said about a document it refused, in the owner's words.
 *
 * The wire vocabulary is closed and deliberately terse; this is where it
 * becomes something actionable. `stale_base` in particular must not read as an
 * error — it is the guard working.
 */
export function rejectionLabel(reason: string): string {
  switch (reason) {
    case 'stale_base':
      return 'Someone else changed this first, so your version was not applied. Reload to see what the boat has now.';
    case 'invalid_row':
      return 'The boat does not recognise one of these mappings — it may be running a different version. Nothing was changed.';
    case 'invalid_egress':
      return 'The chosen gateway cannot transmit on this boat, so nothing was changed.';
    case 'digest_mismatch':
      return 'The boat could not verify the change it received, so nothing was changed. Try saving again.';
    case 'not_authoritative':
      return 'This boat configures its own instrument output, so it did not accept a change from here.';
    default:
      return 'The boat did not accept the last change.';
  }
}
